import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Plus, Minus, Users, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Valores de "porción" para calcular cantidad de personas
const PICADA_VALUES = {
  'salamin': 0.5,
  'chorizo-seco': 1.5,
  'longaniza': 1.5,
  'queso-de-campo': 2
};

// Productos que se pueden incluir en la picada
const PICADA_PRODUCT_SLUGS = ['salamin', 'chorizo-seco', 'longaniza', 'queso-de-campo'];

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(price);
};

const PicadaBuilder = ({ picadaProduct }) => {
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/products`);
        const picadaProducts = response.data.filter(p => 
          PICADA_PRODUCT_SLUGS.includes(p.slug) && p.available && p.stock > 0
        );
        setAvailableProducts(picadaProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Calcular el valor total de la picada para determinar cantidad de personas
  const picadaValue = useMemo(() => {
    return Object.entries(selectedItems).reduce((total, [slug, qty]) => {
      return total + (PICADA_VALUES[slug] || 0) * qty;
    }, 0);
  }, [selectedItems]);

  // Determinar la leyenda de personas
  const getPersonasText = () => {
    if (picadaValue === 0) return null;
    if (picadaValue <= 3) return 'Ideal para 2-3 personas';
    if (picadaValue <= 4.5) return 'Ideal para 4 personas';
    if (picadaValue <= 6) return 'Ideal para 5 personas';
    return 'Ideal para 6 o más personas';
  };

  // Calcular precio total
  const totalPrice = useMemo(() => {
    return Object.entries(selectedItems).reduce((total, [slug, qty]) => {
      const product = availableProducts.find(p => p.slug === slug);
      return total + (product?.price || 0) * qty;
    }, 0);
  }, [selectedItems, availableProducts]);

  // Cantidad total de productos seleccionados
  const totalItems = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);

  // Costo de armado de la picada (viene del producto, default 8000)
  const costoArmado = picadaProduct?.costo_armado || 8000;

  // Precio final con costo de armado
  const finalPrice = totalItems >= 3 ? totalPrice + costoArmado : 0;

  const updateQuantity = (slug, delta) => {
    const product = availableProducts.find(p => p.slug === slug);
    if (!product) return;

    setSelectedItems(prev => {
      const currentQty = prev[slug] || 0;
      const newQty = Math.max(0, Math.min(currentQty + delta, product.stock));
      
      if (newQty === 0) {
        const { [slug]: _, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [slug]: newQty };
    });
  };

  const handleAddToCart = () => {
    if (totalItems < 3) return;

    // Crear descripción de los items incluidos
    const itemsDescription = Object.entries(selectedItems)
      .map(([slug, qty]) => {
        const product = availableProducts.find(p => p.slug === slug);
        return `${qty}x ${product?.name}`;
      })
      .join(', ');

    // Agregar al carrito como un producto personalizado
    const customPicada = {
      slug: `picada-personalizada-${Date.now()}`,
      name: 'Picada Personalizada',
      short_desc: itemsDescription,
      price: totalPrice + costoArmado, // Precio de productos + costo de armado
      image_url: picadaProduct.image_url,
      stock: 1,
      // Guardar los items individuales para el checkout
      picadaItems: selectedItems,
      isPicada: true
    };

    addItem(customPicada);
    setAddedToCart(true);
    
    // Reset después de agregar
    setTimeout(() => {
      setAddedToCart(false);
      setSelectedItems({});
    }, 2000);
  };

  if (loading) {
    return <div className="animate-pulse bg-[#E5D5B4] h-48 rounded-lg"></div>;
  }

  const personasText = getPersonasText();

  return (
    <div className="bg-[#F5EBD4] rounded-lg p-6">
      <h3 className="font-heading text-lg text-[#1E1E1E] mb-2">
        ARMÁ TU PICADA
      </h3>
      <p className="font-body text-sm text-[#4E3A21] mb-4">
        Elegí los productos que querés incluir
      </p>

      {/* Lista de productos disponibles */}
      <div className="space-y-3 mb-6">
        {availableProducts.map(product => {
          const qty = selectedItems[product.slug] || 0;
          const isMaxStock = qty >= product.stock;

          return (
            <div 
              key={product.slug} 
              className="flex items-center gap-3 bg-[#EFE1C4] rounded-lg p-3"
            >
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-14 h-14 object-cover rounded"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-body font-medium text-sm text-[#1E1E1E] truncate">
                  {product.name}
                </h4>
                <p className="font-body text-xs text-[#5C4A2E]">
                  Disponible: {product.stock}
                </p>
              </div>
              
              {/* Controles de cantidad */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQuantity(product.slug, -1)}
                  disabled={qty === 0}
                  className="w-8 h-8 flex items-center justify-center border border-[#8C6A3F] rounded text-[#4E3A21] hover:bg-[#E5D5B4] disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={`Quitar ${product.name}`}
                >
                  <Minus size={14} aria-hidden="true" />
                </button>
                <span className="w-8 text-center font-body font-medium text-[#1E1E1E]" aria-label={`Cantidad de ${product.name}: ${qty}`}>
                  {qty}
                </span>
                <button
                  onClick={() => updateQuantity(product.slug, 1)}
                  disabled={isMaxStock}
                  className="w-8 h-8 flex items-center justify-center border border-[#8C6A3F] rounded text-[#4E3A21] hover:bg-[#E5D5B4] disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={`Agregar ${product.name}`}
                >
                  <Plus size={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Resumen */}
      {totalItems > 0 && (
        <div className="border-t border-[#8C6A3F]/30 pt-4 space-y-3">
          {/* Mensaje si faltan productos o leyenda de personas */}
          {totalItems < 3 ? (
            <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
              <span className="font-body text-sm text-yellow-700">
                Seleccioná al menos 3 productos
              </span>
            </div>
          ) : (
            <>
              {/* Leyenda de personas */}
              {personasText && (
                <div className="flex items-center gap-2 bg-[#7A5A2E]/10 rounded-lg px-4 py-3">
                  <Users size={20} className="text-[#7A5A2E]" />
                  <span className="font-body text-sm text-[#4E3A21] font-medium">
                    {personasText}
                  </span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="font-body text-sm text-[#4E3A21]">
                  {totalItems} productos seleccionados
                </span>
                <span className="font-heading text-xl text-[#7A5A2E]">
                  {formatPrice(finalPrice)}
                </span>
              </div>

              {/* Botón agregar */}
              <Button
                onClick={handleAddToCart}
                disabled={addedToCart}
                className="w-full btn-primary-warm rounded-[10px] h-12 text-base flex items-center justify-center gap-2"
              >
                <ShoppingBag size={18} />
                {addedToCart ? '¡Agregado!' : 'Agregar picada al carrito'}
              </Button>
            </>
          )}
        </div>
      )}

      {totalItems === 0 && (
        <p className="text-center font-body text-sm text-[#4E3A21] opacity-60 py-4">
          Seleccioná al menos 3 productos para armar tu picada
        </p>
      )}
    </div>
  );
};

export default PicadaBuilder;
